import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

const POLL_MS = 3 * 60 * 1000; // 3 minutes

/**
 * Tiny pulsing dot that reflects the live Dropbox connection state.
 * Polled from /api/admin/dropbox/status — only renders when an admin is signed in.
 *
 * States:
 *  - emerald  → connected + healthy
 *  - amber    → connected but unhealthy (token expired / scope issue)
 *  - rose     → not connected
 *  - neutral  → status unknown (initial load / errored)
 */
export default function StorageHealthDot({ isAdmin }) {
  const [tone, setTone] = useState('neutral');
  const [title, setTitle] = useState('Checking storage status…');
  const timer = useRef();

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;

    const refresh = async () => {
      try {
        const s = await api.dropboxStatus();
        if (cancelled) return;
        if (!s.connected) {
          setTone('rose');
          setTitle('Dropbox: not connected');
        } else if (!s.healthy) {
          setTone('amber');
          setTitle('Dropbox: connected but unhealthy');
        } else {
          setTone('emerald');
          setTitle(`Dropbox: connected · ${s.account?.email || ''}`);
        }
      } catch {
        if (cancelled) return;
        setTone('neutral');
        setTitle('Dropbox status unavailable');
      }
    };

    refresh();
    timer.current = setInterval(refresh, POLL_MS);
    return () => {
      cancelled = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, [isAdmin]);

  if (!isAdmin) return null;

  const palette = {
    emerald: 'bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,0.18)]',
    amber: 'bg-amber-400 shadow-[0_0_0_4px_rgba(251,191,36,0.18)]',
    rose: 'bg-rose-500 shadow-[0_0_0_4px_rgba(244,63,94,0.18)]',
    neutral: 'bg-white/30',
  }[tone];

  return (
    <span
      data-testid="storage-health-dot"
      title={title}
      className={`inline-block w-2 h-2 rounded-full ${palette} ${tone === 'emerald' ? 'animate-pulse' : ''}`}
    />
  );
}
